import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useMutation } from '@apollo/client';
import { PDFJSViewerProps, PDFPageRef, createPageRefs, reconcilePageNumbers } from './types';
import { applyPageOperationResult } from './utils/applyPageOperationResult';
import { usePDFRenderer } from './core/PDFRenderer';
import { useViewportManager } from './core/ViewportManager';
import { useReadOnlyMode } from './modes/ReadOnlyMode';
import { ViewerControls } from './ViewerControls';
import { DocumentEditorToolbar, AnnotationTool, FormFieldTool } from './DocumentEditorToolbar';
import { ThumbnailSidebar } from './ThumbnailSidebar';
import { SearchHighlight } from './SearchHighlight';
import { AnnotationOverlay } from './AnnotationOverlay';
import { FieldOverlay } from './FieldOverlay';
import { FormFieldOverlay } from './FormFieldOverlay';
import { CommentsOverlay } from './CommentsOverlay';
import { CommentsSidebar, Comment } from './CommentsSidebar';
import { StrikeoutOverlay, Strikeout } from './StrikeoutOverlay';
import { FreeTextOverlay, FreeText } from './FreeTextOverlay';
import { BuilderFieldsOverlay } from './BuilderFieldsOverlay';
import { FieldContextMenu } from './FieldContextMenu';
import { EditFieldModal } from './EditFieldModal';
import { useDownloadPdf } from './hooks/useDownloadPdf';
import { usePdfSave } from './hooks/usePdfSave';
import { extractFieldsFromPDF, embedFieldsInPDF, UnifiedFormField as ImportedUnifiedFormField } from './utils/pdfFieldUtils';
import { insertPagesFromPDF, deletePage, reorderPages } from './utils/pdfPageUtils';
import { prepareDocumentForSave } from './utils/prepareDocumentForSave';
import { replaceImportDocument, replaceImportDocumentTemplate } from '../../api/mutations';
import { CounterpartyPermissionLevel, canEditPages, canFillForms } from '../../types/counterparty-permissions';
import LoadingSpinner from '../loading-spinner';
import styles from './PDFJSViewer.module.scss';

// Configure PDF.js worker and standard fonts
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const PDFJSViewer: React.FC<PDFJSViewerProps> = ({
  pdfUrl,
  mode = 'read-only',
  initialAnnotations = [],
  onAnnotationsChange,
  onFieldClick,
  onPagesReady,
  onDocumentLoad,
  onPagesLoad,
  showControls = true,
  showThumbnails = true,
  minZoom = 0.5,
  maxZoom = 3.0,
  className,
  fieldOverlayProps,
  formFieldOverlayProps,
  resourceType = 'document', // 'document' or 'template'
  documentId = '',
  teamId = '',
  documentTitle = 'Document',
  lastSaved,
  counterpartyPermissionLevel = CounterpartyPermissionLevel.CommentOnly,
  effectivePermissionLevel, // If not provided, falls back to counterpartyPermissionLevel
  canAccessFormsToggle = false, // Whether to show Annotate/Forms toggle (creator team with edit permission only)
  onChangePermissions,
  canEditPermissions = false,
  hideComments = false,
  hideFormFieldsAndAnnotations = false,
  currentUserId,
  currentUserName,
  savedPdfFormFields = [],
  savedPdfAnnotations = [],
  onPdfSaveSuccess,
}) => {
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Zoom state managed here to coordinate between PDFRenderer and ViewportManager
  const [zoom, setZoom] = useState(1);
  const [isZooming, setIsZooming] = useState(false); // Hide overlays during zoom transitions
  const [thumbnailsVisible, setThumbnailsVisible] = useState(false); // Closed by default

  // Determine the actual permission level to use for UI controls
  // If effectivePermissionLevel is provided, use it (creator override)
  // Otherwise, use counterpartyPermissionLevel
  const activePermissionLevel = effectivePermissionLevel || counterpartyPermissionLevel;

  // Determine if form fields should be read-only based on mode AND permission level
  // Form fields are read-only if: (1) viewer is in read-only mode, OR (2) user lacks form filling permission
  const formFieldsReadOnly = mode === 'read-only' || !canFillForms(activePermissionLevel);

  // Edit mode state
  const [formBuilderActive, setFormBuilderActive] = useState(false);
  const [activeAnnotationTool, setActiveAnnotationTool] = useState<AnnotationTool>('none');
  const [activeFormFieldTool, setActiveFormFieldTool] = useState<FormFieldTool>('none');
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [pages, setPages] = useState<PDFPageRef[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [strikeouts, setStrikeouts] = useState<Strikeout[]>([]);
  const [freeTexts, setFreeTexts] = useState<FreeText[]>([]);

  // Track if we've loaded initial data from backend
  const hasLoadedAnnotationsRef = useRef(false);

  // Hide overlays during zoom transitions, show when settled
  useEffect(() => {
    setIsZooming(true);
    const debounceTimer = setTimeout(() => {
      setIsZooming(false);
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [zoom]);

  useEffect(() => {
    if (!hasLoadedAnnotationsRef.current && savedPdfAnnotations && savedPdfAnnotations.length > 0 && pages.length > 0) {
      const loadedComments: Comment[] = [];
      const loadedStrikeouts: Strikeout[] = [];
      const loadedFreeTexts: FreeText[] = [];

      savedPdfAnnotations.forEach((annotation: any) => {
        const annotationPageId = pages.find(p => p.position === annotation.page)?.id ?? `temp-page-${annotation.page}`;

        if (annotation.type === 'comment') {
          loadedComments.push({
            id: annotation.id,
            pageId: annotationPageId,
            pageNum: annotation.page,
            x: annotation.x,
            y: annotation.y,
            text: annotation.text,
            author: annotation.author,
            authorId: annotation.authorId,
            createdAt: annotation.createdAt,
            parentId: annotation.parentId,
          });
        } else if (annotation.type === 'strikeout') {
          loadedStrikeouts.push({
            id: annotation.id,
            pageId: annotationPageId,
            pageNum: annotation.page,
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height || 1.5,
            color: annotation.color || '#dc3545',
            text: annotation.text || '',
            groupId: annotation.groupId || annotation.id,
          });
        } else if (annotation.type === 'freetext') {
          loadedFreeTexts.push({
            id: annotation.id,
            pageId: annotationPageId,
            pageNum: annotation.page,
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height,
            text: annotation.text,
            fontSize: annotation.fontSize || 12,
            fontColor: annotation.fontColor || '#000000',
          });
        }
      });

      setComments(loadedComments);
      setStrikeouts(loadedStrikeouts);
      setFreeTexts(loadedFreeTexts);

      hasLoadedAnnotationsRef.current = true;
    }
  }, [savedPdfAnnotations, pages]);

  type UnifiedFormField = ImportedUnifiedFormField;
  const [allFields, setAllFields] = useState<UnifiedFormField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState<string | null>(null);
  const [isPageOperationInProgress, setIsPageOperationInProgress] = useState(false);
  const [dirtyCounters, setDirtyCounters] = useState({
    comments: 0,
    strikeouts: 0,
    freeTexts: 0,
    fields: 0,
  });
  const [pageRevision, setPageRevision] = useState(0);

  const markDirty = useCallback((type: keyof typeof dirtyCounters) => {
    setDirtyCounters(prev => ({ ...prev, [type]: prev[type] + 1 }));
  }, []);

  useEffect(() => {
    return () => {
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
      }
    };
  }, [modifiedPdfUrl]);

  const [contextMenuState, setContextMenuState] = useState<{
    fieldId: string;
    x: number;
    y: number;
  } | null>(null);
  const [editingField, setEditingField] = useState<UnifiedFormField | null>(null);

  const userId = currentUserId;
  const userName = currentUserName;
  const [strikeoutHistory, setStrikeoutHistory] = useState<string[]>([]);

  const handleAddStrikeout = useCallback((strikeout: Omit<Strikeout, 'id' | 'pageId'>) => {
    const strikeoutPageId = pages.find(p => p.position === strikeout.pageNum)?.id ?? `temp-page-${strikeout.pageNum}`;

    const newStrikeout: Strikeout = {
      ...strikeout,
      id: `strikeout-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      pageId: strikeoutPageId,
    };
    setStrikeouts((prev) => [...prev, newStrikeout]);
    setStrikeoutHistory((prev) => [...prev.slice(-9), newStrikeout.id]);
  }, [pages]);

  const handleDeleteStrikeout = useCallback((strikeoutId: string) => {
    setStrikeouts((prev) => prev.filter((s) => s.id !== strikeoutId));
    markDirty('strikeouts');
  }, [markDirty]);

  const effectivePdfUrl = modifiedPdfUrl || pdfUrl;

  const {
    pdfDocument,
    pagesInfo,
    totalPages,
    isLoading,
    error,
    renderPage,
  } = usePDFRenderer({
    pdfUrl: effectivePdfUrl,
    zoom,
  });

  useEffect(() => {
    if (pdfDocument && pdfDocument.numPages > 0 && pages.length === 0) {
      const newPages = createPageRefs(pdfDocument.numPages);
      setPages(newPages);
    }
  }, [pdfDocument, pages.length]);

  useEffect(() => {
    if (pages.length > 0 && onPagesLoad) {
      onPagesLoad(pages);
    }
  }, [pages, onPagesLoad]);

  // Track whether we've loaded fields for the current document to prevent redundant loads
  const fieldsLoadedRef = useRef(false);

  // Keep a ref to savedPdfFormFields to avoid dependency issues
  const savedFieldsRef = useRef(savedPdfFormFields);
  useEffect(() => {
    savedFieldsRef.current = savedPdfFormFields;
  }, [savedPdfFormFields]);

  useEffect(() => {
    if (!effectivePdfUrl) return;

    const controller = new AbortController();
    let mounted = true;

    const loadPdfBytesAndFields = async () => {
      try {
        const response = await fetch(effectivePdfUrl, { signal: controller.signal });
        const arrayBuffer = await response.arrayBuffer();

        if (!mounted) return;

        const bytes = new Uint8Array(arrayBuffer);
        setPdfBytes(bytes);

        // UNIFIED FIELD LOADING: Backend-first approach (single source of truth)
        // Load fields only once when PDF is first loaded (not on modified PDF URLs)
        // CRITICAL: Wait for pages to be created before loading fields
        if (!fieldsLoadedRef.current && !modifiedPdfUrl && pages.length > 0) {
          const currentSavedFields = savedFieldsRef.current;

          // Build pageId mapping for Two-Phase tracking (pages array must exist)
          const pageIdMap = new Map(pages.map(p => [p.position, p.id]));

          if (currentSavedFields && currentSavedFields.length > 0) {
            // Backend fields exist - use them (source of truth)
            const loadedFields: ImportedUnifiedFormField[] = currentSavedFields.map((backendField: any) => {
              const fieldPageId = pageIdMap.get(backendField.page);

              // VALIDATION: All fields must have valid pageIds (no fallback to temp)
              if (!fieldPageId) {
                console.error(`Field on page ${backendField.page} has no matching pageId. Pages available: ${pages.map(p => p.position).join(', ')}`);
                throw new Error(`Invalid field data: page ${backendField.page} does not exist`);
              }

              // Normalize checkbox values from JSONB to boolean for consistency
              let normalizedValue = backendField.value;
              if (backendField.type === 'checkbox') {
                const rawValue = backendField.value;
                normalizedValue = rawValue === true || rawValue === 'true' || rawValue === 1 ||
                                 rawValue === 'Yes' || rawValue === 'On' || rawValue === 'yes' || rawValue === 'on';
              }

              return {
                id: backendField.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                type: backendField.type === 'textfield' ? 'textfield' : backendField.type,
                pageId: fieldPageId,
                pageNum: backendField.page,
                x: backendField.x,
                y: backendField.y,
                width: backendField.width,
                height: backendField.height,
                value: normalizedValue,  // Normalized checkbox values to boolean
                fieldName: backendField.fieldName,
                placeholder: backendField.placeholder,
                multiline: backendField.multiline,
                horizontalAlign: backendField.horizontalAlign || 'left',
                verticalAlign: backendField.verticalAlign || 'middle',
                fontSize: backendField.fontSize || 12,
                fontColor: backendField.fontColor || '#000000',
                readOnly: backendField.readOnly || false,
                required: backendField.required || false,
              };
            });

            setAllFields(loadedFields);
            fieldsLoadedRef.current = true;
          } else {
            // No backend fields - extract from PDF (initial upload)
            const extractedFields = await extractFieldsFromPDF(bytes.slice(), pageIdMap, allFields);
            setAllFields(extractedFields);
            fieldsLoadedRef.current = true;
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to load PDF bytes and fields:', err);
        }
      }
    };

    loadPdfBytesAndFields();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [effectivePdfUrl, formBuilderActive, modifiedPdfUrl, pages.length]);

  useEffect(() => {
    hasLoadedAnnotationsRef.current = false;
    fieldsLoadedRef.current = false;
  }, [documentId, pdfUrl]);

  // Core: Viewport management (zoom, scroll, navigation)
  const {
    currentPage,
    setCurrentPage,
    zoomIn,
    zoomOut,
    resetZoom,
    fitWidth,
    goToPage,
  } = useViewportManager({
    pagesInfo,
    containerRef: pagesContainerRef,
    minZoom,
    maxZoom,
    zoom,
    setZoom,
  });

  // Mode: Read-only behavior (search, view annotations)
  const {
    searchMatches,
    currentSearchResultIndex,
    handleSearch,
    nextSearchResult,
    previousSearchResult,
  } = useReadOnlyMode({
    pdfDocument,
    pagesInfo,
    annotations: initialAnnotations,
    onAnnotationClick: onFieldClick,
  });

  const { downloadPdf } = useDownloadPdf({
    pdfUrl,
    pdfDocument,
    documentTitle,
    annotations: initialAnnotations,
  });

  const { saveAll } = usePdfSave({
    documentId,
    resourceType, // 'document' or 'template' - determines which mutations to use
    onSaveSuccess: (timestamp) => {
      onPdfSaveSuccess?.(timestamp); // Notify parent with timestamp from mutation response
    },
    onSaveError: (error) => console.error('Error saving PDF data:', error),
    debounceMs: 1000, // 1 second debounce
  });

  // PDF upload mutations for page insert/delete/reorder operations
  const [uploadDocumentPdf] = useMutation(replaceImportDocument, {
    onError: (error) => console.error('Error uploading modified document PDF:', error),
  });

  const [uploadTemplatePdf] = useMutation(replaceImportDocumentTemplate, {
    onError: (error) => console.error('Error uploading modified template PDF:', error),
  });

  /**
   * Upload modified PDF to backend after page insert/delete/reorder operations
   */
  const uploadModifiedPdf = useCallback(async (pdfBytes: Uint8Array) => {
    if (!documentId || !teamId) {
      return;
    }

    try {
      // Convert Uint8Array to base64 string
      const base64String = btoa(
        Array.from(pdfBytes)
          .map(byte => String.fromCharCode(byte))
          .join('')
      );

      // Create file data object matching BinaryFileInputType
      const fileData = {
        filename: `${documentTitle}.pdf`,
        contentType: 'application/pdf',
        content: base64String,
        contentEncodingType: 'base64', // Required by BinaryFileInputType
      };

      // Upload using appropriate mutation based on resource type
      if (resourceType === 'template') {
        await uploadTemplatePdf({
          variables: {
            templateId: documentId,
            fileData,
            teamId,
          },
        });
      } else {
        await uploadDocumentPdf({
          variables: {
            documentId,
            fileData,
            teamId,
          },
        });
      }
    } catch (error) {
      console.error('Failed to upload modified PDF:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }, [documentId, teamId, documentTitle, resourceType, uploadDocumentPdf, uploadTemplatePdf]);

  useEffect(() => {
    if ((mode === 'edit' || mode === 'review' || formBuilderActive) && documentId) {
      const { formFields, comments: pdfComments, strikeouts: pdfStrikeouts, freeTexts: pdfFreeTexts } =
        prepareDocumentForSave(allFields, comments, strikeouts, freeTexts);

      saveAll(formFields, pdfComments, pdfStrikeouts, pdfFreeTexts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyCounters, mode, documentId, formBuilderActive, allFields, comments, strikeouts, freeTexts]);

  const handleDownloadPdfWithFields = useCallback(async () => {
    if (!pdfBytes) {
      console.error('PDF bytes not loaded');
      return;
    }

    try {
      const pdfWithFields = await embedFieldsInPDF(
        pdfBytes,
        allFields,
        comments,
        strikeouts,
        freeTexts
      );
      const blob = new Blob([pdfWithFields.buffer as ArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentTitle || 'document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF with fields:', err);
    }
  }, [pdfBytes, allFields, comments, strikeouts, freeTexts, documentTitle]);

  // Toolbar handlers
  const handleToggleFormBuilder = useCallback(() => {
    if (formBuilderActive && documentId) {
      setAllFields((currentFields) => {
        const { formFields, comments: pdfComments, strikeouts: pdfStrikeouts, freeTexts: pdfFreeTexts } =
          prepareDocumentForSave(currentFields, comments, strikeouts, freeTexts);

        saveAll(formFields, pdfComments, pdfStrikeouts, pdfFreeTexts);
        return currentFields;
      });
    }

    setFormBuilderActive((prev) => !prev);
    // When toggling to form builder, clear annotation tool selection
    if (!formBuilderActive) {
      setActiveAnnotationTool('none');
    } else {
      setActiveFormFieldTool('none');
    }
  }, [formBuilderActive, documentId, saveAll, comments, strikeouts, freeTexts]);

  const handleSelectAnnotationTool = useCallback((tool: AnnotationTool) => {
    setActiveAnnotationTool(tool);
  }, []);

  const handleSelectFormFieldTool = useCallback((tool: FormFieldTool) => {
    setActiveFormFieldTool(tool);
  }, []);

  const handleChangePermissions = useCallback((level: CounterpartyPermissionLevel) => {
    if (onChangePermissions) {
      onChangePermissions(level);
    }
  }, [onChangePermissions]);

  const handleToggleComments = useCallback(() => {
    setCommentsVisible((prev) => !prev);
  }, []);

  // Page manipulation handlers
  const handleInsertPage = useCallback(async (afterPageNumber: number, pdfFile: File) => {
    if (!pdfBytes) {
      console.error('PDF bytes not loaded');
      return;
    }

    setIsPageOperationInProgress(true);
    try {
      const uploadedPdfBytes = await pdfFile.arrayBuffer();
      // CRITICAL: Create independent copies BEFORE any API that might detach the ArrayBuffer
      // pdfjs-dist's getDocument() transfers the ArrayBuffer to a worker, detaching the original
      const insertCopy = new Uint8Array(uploadedPdfBytes).slice();      // For insertPagesFromPDF
      const countCopy = new Uint8Array(uploadedPdfBytes).slice();       // For getDocument (page count)
      const extractCopy = new Uint8Array(uploadedPdfBytes).slice();
      const { pdfBytes: newPdfBytes } = await insertPagesFromPDF(
        pdfBytes,
        insertCopy,
        afterPageNumber
      );

      const insertPdfDoc = await pdfjsLib.getDocument({ data: countCopy }).promise;
      const insertedPageCount = insertPdfDoc.numPages;

      const newPageRefs = Array.from({ length: insertedPageCount }, (_, i) => ({
        id: crypto.randomUUID(),
        position: afterPageNumber + i + 1,
      }));

      const sortedPages = [...pages].sort((a, b) => a.position - b.position);
      sortedPages.splice(afterPageNumber, 0, ...newPageRefs);

      const updatedPages: PDFPageRef[] = sortedPages.map((p, i) => ({
        id: p.id,
        position: i + 1
      }));

      const reconciledComments = reconcilePageNumbers(comments, updatedPages);
      const reconciledStrikeouts = reconcilePageNumbers(strikeouts, updatedPages);
      const reconciledFreeTexts = reconcilePageNumbers(freeTexts, updatedPages);
      const reconciledBuilderFields = reconcilePageNumbers(allFields, updatedPages);

      const insertPageIdMap = new Map(newPageRefs.map(p => [p.position - afterPageNumber, p.id]));
      const insertedPdfFields = await extractFieldsFromPDF(extractCopy, insertPageIdMap, reconciledBuilderFields);

      const adjustedInsertedFields = insertedPdfFields.map(field => ({
        ...field,
        pageNum: afterPageNumber + field.pageNum,
      }));

      const mergedFields = [...reconciledBuilderFields, ...adjustedInsertedFields];

      await applyPageOperationResult({
        newPdfBytes,
        updatedPages,
        reconciledFields: mergedFields,
        reconciledComments,
        reconciledStrikeouts,
        reconciledFreeTexts,
        documentId,
        setPages,
        setComments,
        setStrikeouts,
        setFreeTexts,
        setAllFields,
        setPdfBytes,
        setModifiedPdfUrl,
        setPageRevision,
        saveAll,
        markDirty,
        uploadModifiedPdf,
        prepareDocumentForSave,
      });
    } catch (error) {
      console.error('Failed to insert pages:', error);
    } finally {
      setIsPageOperationInProgress(false);
    }
  }, [pdfBytes, comments, strikeouts, freeTexts, allFields, documentId, saveAll, uploadModifiedPdf, pages, markDirty]);

  const handleDeletePage = useCallback(async (pageNumber: number) => {
    if (!pdfBytes) {
      console.error('PDF bytes not loaded');
      return;
    }

    setIsPageOperationInProgress(true);
    try {
      const { pdfBytes: newPdfBytes } = await deletePage(pdfBytes, pageNumber);

      const updatedPages: PDFPageRef[] = pages
        .filter(p => p.position !== pageNumber)
        .map(p => ({
          id: p.id,
          position: p.position > pageNumber ? p.position - 1 : p.position,
        }));

      const reconciledComments = reconcilePageNumbers(comments, updatedPages);
      const reconciledStrikeouts = reconcilePageNumbers(strikeouts, updatedPages);
      const reconciledFreeTexts = reconcilePageNumbers(freeTexts, updatedPages);
      const reconciledBuilderFields = reconcilePageNumbers(allFields, updatedPages);

      await applyPageOperationResult({
        newPdfBytes,
        updatedPages,
        reconciledFields: reconciledBuilderFields,
        reconciledComments,
        reconciledStrikeouts,
        reconciledFreeTexts,
        documentId,
        setPages,
        setComments,
        setStrikeouts,
        setFreeTexts,
        setAllFields,
        setPdfBytes,
        setModifiedPdfUrl,
        saveAll,
        markDirty,
        uploadModifiedPdf,
        prepareDocumentForSave,
      });
    } catch (error) {
      console.error('Failed to delete page:', error);
    } finally {
      setIsPageOperationInProgress(false);
    }
  }, [pdfBytes, comments, strikeouts, freeTexts, allFields, documentId, saveAll, uploadModifiedPdf, markDirty, pages]);

  const handleReorderPages = useCallback(async (fromPageNumber: number, toPageNumber: number) => {
    if (!pdfBytes) {
      console.error('PDF bytes not loaded');
      return;
    }

    setIsPageOperationInProgress(true);
    try {
      const { pdfBytes: newPdfBytes } = await reorderPages(
        pdfBytes,
        fromPageNumber,
        toPageNumber
      );

      const sortedPages = [...pages].sort((a, b) => a.position - b.position);
      const fromIdx = fromPageNumber - 1;
      const toIdx = toPageNumber - 1;
      const [movedPage] = sortedPages.splice(fromIdx, 1);
      sortedPages.splice(toIdx, 0, movedPage);

      const updatedPages: PDFPageRef[] = sortedPages.map((p, i) => ({
        id: p.id,
        position: i + 1
      }));

      const reconciledComments = reconcilePageNumbers(comments, updatedPages);
      const reconciledStrikeouts = reconcilePageNumbers(strikeouts, updatedPages);
      const reconciledFreeTexts = reconcilePageNumbers(freeTexts, updatedPages);
      const reconciledBuilderFields = reconcilePageNumbers(allFields, updatedPages);

      await applyPageOperationResult({
        newPdfBytes,
        updatedPages,
        reconciledFields: reconciledBuilderFields,
        reconciledComments,
        reconciledStrikeouts,
        reconciledFreeTexts,
        documentId,
        setPages,
        setComments,
        setStrikeouts,
        setFreeTexts,
        setAllFields,
        setPdfBytes,
        setModifiedPdfUrl,
        saveAll,
        markDirty,
        uploadModifiedPdf,
        prepareDocumentForSave,
      });
    } catch (error) {
      console.error('Failed to reorder pages:', error);
    } finally {
      setIsPageOperationInProgress(false);
    }
  }, [pdfBytes, comments, strikeouts, freeTexts, allFields, documentId, saveAll, uploadModifiedPdf, markDirty, pages]);

  // Comment handlers
  // Handler adds id, createdAt, and pageId
  const handleAddComment = useCallback((comment: Omit<Comment, 'id' | 'createdAt' | 'pageId'>) => {
    // Get pageId for this comment's page
    const commentPageId = pages.find(p => p.position === comment.pageNum)?.id ?? `temp-page-${comment.pageNum}`;

    const newComment: Comment = {
      ...comment,
      id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      pageId: commentPageId,
      createdAt: new Date().toISOString(),
    };
    setComments((prev) => [...prev, newComment]);
    // Note: Autosave effect will handle saving complete state
  }, [pages]);

  const handleEditComment = useCallback(async (commentId: string, newText: string) => {
    // Update local state optimistically
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === commentId
          ? { ...comment, text: newText, modifiedAt: new Date().toISOString() }
          : comment
      )
    );
    // Note: Autosave effect will handle saving complete state
  }, []);

  const handleUpdateComment = useCallback(async (commentId: string, updates: Partial<Comment>) => {
    // Update local state optimistically - this will trigger autosave via useEffect
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === commentId
          ? { ...comment, ...updates, modifiedAt: new Date().toISOString() }
          : comment
      )
    );

    // Note: No immediate save here - the autosave effect will handle it
    // This prevents race conditions where we save partial state during drag
    // and the debounced autosave saves the full state with old positions
  }, []);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    // Remove from local state optimistically
    setComments((prev) => prev.filter((comment) => comment.id !== commentId));
    // Note: Autosave effect will handle saving complete state
  }, []);

  const handleCommentClick = useCallback((comment: Comment) => {
    // Open sidebar if not already open
    if (!commentsVisible) {
      setCommentsVisible(true);
    }
    // Scroll to the page containing the comment
    goToPage(comment.pageNum);
    // Highlight the comment marker
    setSelectedCommentId(comment.id);
    // Clear highlight after 2 seconds
    setTimeout(() => setSelectedCommentId(null), 2000);
  }, [goToPage, commentsVisible]);

  const handleAddCommentAt = useCallback((pageNum: number, x: number, y: number) => {
    // Get pageId for this comment's page
    const commentPageId = pages.find(p => p.position === pageNum)?.id ?? `temp-page-${pageNum}`;

    // Create comment marker immediately with empty text
    const newComment: Comment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      pageId: commentPageId,
      pageNum,
      x,
      y,
      text: '', // Will be filled in sidebar
      author: userName,
      authorId: userId,
      createdAt: new Date().toISOString(),
    };

    // Add to state
    setComments(prev => [...prev, newComment]);

    // DISABLE add comment mode immediately to prevent multiple clicks
    setActiveAnnotationTool('none');

    // Auto-open sidebar
    setCommentsVisible(true);

    // Set as selected to trigger edit mode in sidebar
    setSelectedCommentId(newComment.id);

  }, [userId, userName, pages]);

  // Free text handlers
  const handleAddFreeTextAt = useCallback((pageNum: number, x: number, y: number) => {
    // Get pageId for this free text's page
    const freeTextPageId = pages.find(p => p.position === pageNum)?.id ?? `temp-page-${pageNum}`;

    const newFreeText: FreeText = {
      id: `freetext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      pageId: freeTextPageId,
      pageNum,
      x,
      y,
      width: 200,
      height: 15, // Single line minimum height (matches resize constraint)
      text: 'Click to edit text', // Placeholder that gets auto-selected
      fontSize: 14,
      fontColor: '#000000',
    };
    setFreeTexts((prev) => [...prev, newFreeText]);
  }, [pages]);

  const handleUpdateFreeText = useCallback((id: string, text: string) => {
    setFreeTexts((prev) =>
      prev.map((ft) =>
        ft.id === id ? { ...ft, text } : ft
      )
    );
  }, []);

  const handleUpdateFreeTextProperties = useCallback((id: string, updates: Partial<FreeText>) => {
    setFreeTexts((prev) =>
      prev.map((ft) =>
        ft.id === id ? { ...ft, ...updates } : ft
      )
    );
  }, []);

  const handleMoveFreeText = useCallback((id: string, x: number, y: number) => {
    setFreeTexts((prev) =>
      prev.map((ft) =>
        ft.id === id ? { ...ft, x, y } : ft
      )
    );
  }, []);

  const handleResizeFreeText = useCallback((id: string, width: number, height: number) => {
    setFreeTexts((prev) =>
      prev.map((ft) =>
        ft.id === id ? { ...ft, width, height } : ft
      )
    );
  }, []);

  const handleDeleteFreeText = useCallback((id: string) => {
    setFreeTexts((prev) => prev.filter((ft) => ft.id !== id));
  }, []);

  // Builder form field handlers
  const handleFieldClick = useCallback((fieldId: string, x: number, y: number) => {
    setContextMenuState({ fieldId, x, y });
  }, []);

  const handleEditField = useCallback(() => {
    if (!contextMenuState) return;

    const field = allFields.find(f => f.id === contextMenuState.fieldId);
    if (field) {
      setEditingField(field);
    }
  }, [contextMenuState, allFields]);

  const handleDeleteField = useCallback(() => {
    if (!contextMenuState) return;

    setAllFields(prev => prev.filter(f => f.id !== contextMenuState.fieldId));
  }, [contextMenuState]);

  const handleSaveFieldEdits = useCallback((updates: Partial<UnifiedFormField>) => {
    if (!editingField) return;

    setAllFields(prev => {
      const updated = prev.map(f => {
        if (f.id === editingField.id) {
          return { ...f, ...updates };
        }
        return f;
      });
      return updated;
    });
  }, [editingField]);

  const handleFieldPositionChange = useCallback((fieldId: string, x: number, y: number) => {
    setAllFields(prev =>
      prev.map(f =>
        f.id === fieldId ? { ...f, x, y } : f
      )
    );
  }, []);

  const handleFieldSizeChange = useCallback((fieldId: string, width: number, height: number) => {
    setAllFields(prev =>
      prev.map(f =>
        f.id === fieldId ? { ...f, width, height } : f
      )
    );
  }, []);

  /**
   * Handle field value updates (for interactive form filling)
   * UNIFIED ARCHITECTURE: Updates value in allFields (single source of truth)
   */
  const handleUpdateFieldValue = useCallback((fieldId: string, value: any) => {
    setAllFields(prev => {
      const edited = prev.find(f => f.id === fieldId);
      if (!edited) return prev;
      // Fan out to linked fields: same fieldName + same type = same logical PDF field.
      // Skip auto-generated names (`field_…`) so solo fields stay solo.
      const linkKey = edited.fieldName;
      const shouldLink = !!linkKey && !linkKey.startsWith('field_');
      return prev.map(field =>
        field.id === fieldId ||
        (shouldLink && field.fieldName === linkKey && field.type === edited.type)
          ? { ...field, value }
          : field
      );
    });
    markDirty('fields'); // Trigger autosave
  }, [markDirty]);

  /**
   * Handle field selection
   */
  const handleFieldSelect = useCallback((fieldId: string) => {
    setSelectedFieldId(fieldId);
  }, []);

  /**
   * Handle page clicks for placing annotations (form fields, free text, etc.)
   */
  const handlePageClick = useCallback((e: React.MouseEvent<HTMLDivElement>, pageNum: number) => {
    // Don't handle if clicking on existing annotations or during signing
    if (hideComments) return;

    const target = e.target as HTMLElement;
    // Ignore if clicking on an existing element (marker, field, etc.)
    if (target.closest('.commentMarker, .strikeoutLine, .annotation')) return;

    const containerRect = e.currentTarget.getBoundingClientRect();
    // Convert screen pixels to PDF coordinates by dividing by zoom
    const x = (e.clientX - containerRect.left) / zoom;
    const y = (e.clientY - containerRect.top) / zoom;

    // Handle different tools
    if (activeAnnotationTool === 'comment') {
      // Comment placement is handled by CommentsOverlay to avoid duplicates
      // Don't call handleAddCommentAt here
      return;
    } else if (activeAnnotationTool === 'freetext') {
      // Create free text annotation - handled by FreeTextOverlay
      // The overlay will call handleAddFreeTextAt when clicked
      // Note: We don't deactivate the tool here - overlay handles it
    } else if (activeFormFieldTool === 'textfield') {
      // Create text field (coordinates in PDF space, dimensions at 1.0 zoom)
      // Get pageId for this page
      const currentPageId = pages.find(p => p.position === pageNum)?.id ?? `temp-page-${pageNum}`;

      const newField: UnifiedFormField = {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'textfield',
        pageId: currentPageId,
        pageNum,
        x,
        y,
        width: 150,
        height: 24,
        placeholder: 'Text field',
        // Include default alignment properties for new fields
        horizontalAlign: 'left',
        verticalAlign: 'middle',
        fontSize: 12,
        fontColor: '#000000',
      };
      setAllFields(prev => [...prev, newField]);
      setActiveFormFieldTool('none');
    } else if (activeFormFieldTool === 'checkbox') {
      // Create checkbox (coordinates in PDF space, centered on click)
      // Get pageId for this page
      const currentPageId = pages.find(p => p.position === pageNum)?.id ?? `temp-page-${pageNum}`;

      const newField: UnifiedFormField = {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'checkbox',
        pageId: currentPageId,
        pageNum,
        x: x - 8, // Center on click
        y: y - 8,
        width: 16,
        height: 16,
      };
      setAllFields(prev => [...prev, newField]);
      setActiveFormFieldTool('none');
    }
  }, [activeAnnotationTool, activeFormFieldTool, hideComments, zoom]);

  /**
   * Notify parent when pages are ready
   */
  useEffect(() => {
    if (pagesInfo.length > 0 && onPagesReady) {
      onPagesReady(pagesInfo);
    }
  }, [pagesInfo, onPagesReady]);

  /**
   * Notify parent when PDF document is loaded
   */
  useEffect(() => {
    if (pdfDocument && onDocumentLoad) {
      onDocumentLoad(pdfDocument);
    }
  }, [pdfDocument, onDocumentLoad]);

  /**
   * Auto fit-to-width on mobile screens to prevent horizontal scrolling
   * Desktop: load at 100% zoom
   * Mobile (≤768px): auto-fit to screen width
   */
  useEffect(() => {
    if (pagesInfo.length === 0 || zoom !== 1) return;

    // Check if mobile screen (common breakpoint)
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // Safari mobile-specific fix: Delay fitWidth to allow layout to settle
      // Safari's layout engine needs time to complete flexbox calculations before measuring container width
      // Without this delay, Safari may return incorrect clientWidth values, causing wrong zoom (often 64%)
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      if (isSafari) {
        // Use requestAnimationFrame + setTimeout to ensure layout is complete
        requestAnimationFrame(() => {
          setTimeout(() => {
            fitWidth();
          }, 100);
        });
      } else {
        // Other mobile browsers can fit immediately
        fitWidth();
      }
    }
    // On desktop, keep default 100% zoom

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Render pages to canvases when pagesInfo changes (including zoom/scale changes)
   * Create stable dependency by stringifying page dimensions
   */
  const pagesDimensionsKey = React.useMemo(() => {
    return pagesInfo.map(p => `${p.pageNumber}-${p.width}-${p.height}-${p.scale}`).join(',');
  }, [pagesInfo]);

  useEffect(() => {
    if (!pdfDocument || !pagesContainerRef.current || pagesInfo.length === 0) {
      return;
    }

    let cancelled = false;

    const renderAllPages = async () => {
      for (const pageInfo of pagesInfo) {
        if (cancelled) break;
        await renderPage(pageInfo.pageNumber, `pdf-page-${pageInfo.pageNumber}`);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const frameId = requestAnimationFrame(() => {
      if (!cancelled) renderAllPages();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [pdfDocument, pagesDimensionsKey, renderPage]);

  // Strikeout mode - Use native PDF.js text selection (react-pdf-highlighter approach)
  useEffect(() => {
    const strikeoutActive = activeAnnotationTool === 'strikeout';
    const container = containerRef.current;
    let selectionTimeout: NodeJS.Timeout | null = null;

    // Function to apply/remove strikeout mode to all text layers
    const updateTextLayers = () => {
      const textLayers = document.querySelectorAll('.textLayer');
      if (strikeoutActive) {
        textLayers.forEach((layer: Element) => {
          const htmlLayer = layer as HTMLElement;
          htmlLayer.classList.add('strikeout-mode');
          // Apply strikeout mode styles - CSS handles most of it
          // Note: pointer-events approach doesn't prevent whitespace selection
          // because whitespace is INSIDE the span content, not between spans
          // We handle whitespace trimming in the rect processing logic instead
        });
      } else {
        textLayers.forEach((layer: Element) => {
          const htmlLayer = layer as HTMLElement;
          htmlLayer.classList.remove('strikeout-mode');
        });
      }
    };

    // Initial update
    updateTextLayers();

    // Re-apply when text layers are added (after page renders)
    // Use MutationObserver to detect new text layers
    const observer = new MutationObserver(() => {
      if (strikeoutActive) {
        updateTextLayers();
      }
    });

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    if (!strikeoutActive) {
      observer.disconnect();
      return;
    }

    // Handle mouseup - only process selection when user finishes selecting
    const handleMouseUp = () => {
      // Small delay to ensure selection is finalized
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        if (range.collapsed) return;

        // Validate selection is within PDF container
        if (!container || !container.contains(range.commonAncestorContainer)) return;

        processStrikeoutSelection(range, selection);
      }, 50); // Short delay to ensure selection is complete
    };

    // Type definitions for rect handling
    type LocalRect = { left: number; top: number; width: number; height: number };

    /**
     * React-PDF-Highlighter's Proven Approach
     * Simple, direct rect extraction from range.getClientRects()
     * Source: https://github.com/agentcooper/react-pdf-highlighter
     */

    /**
     * Extract client rects from range and convert to local coordinates
     * Based on react-pdf-highlighter's get-client-rects.ts
     */
    const getClientRectsForRange = (range: Range, canvasContainer: HTMLElement): LocalRect[] => {
      const containerRect = canvasContainer.getBoundingClientRect();
      const clientRects = Array.from(range.getClientRects());

      const rects: LocalRect[] = [];

      for (const clientRect of clientRects) {
        // Validate rect is inside page bounds and has valid dimensions
        if (
          clientRect.width > 0 &&
          clientRect.height > 0 &&
          clientRect.width < containerRect.width &&
          clientRect.height < containerRect.height
        ) {
          rects.push({
            left: clientRect.left - containerRect.left,
            top: clientRect.top - containerRect.top,
            width: clientRect.width,
            height: clientRect.height,
          });
        }
      }

      return rects;
    };

    /**
     * Optimize/merge adjacent rects
     * Based on react-pdf-highlighter's optimize-client-rects.ts
     */
    const optimizeClientRects = (clientRects: LocalRect[]): LocalRect[] => {
      if (!clientRects.length) return [];

      const rects = clientRects.slice().sort((a, b) => {
        if (Math.abs(a.top - b.top) < 5) {
          return a.left - b.left;
        }
        return a.top - b.top;
      });

      // Helper functions from react-pdf-highlighter
      const sameLine = (A: LocalRect, B: LocalRect, yMargin = 5) =>
        Math.abs(A.top - B.top) < yMargin && Math.abs(A.height - B.height) < yMargin;

      const overlaps = (A: LocalRect, B: LocalRect) =>
        A.left <= B.left && B.left <= A.left + A.width;

      const nextTo = (A: LocalRect, B: LocalRect, xMargin = 1) => {
        const Aright = A.left + A.width;
        const Bright = B.left + B.width;
        // 10px was merging rects across whitespace, extending strikeouts past actual text
        // 1px only merges truly adjacent characters/words
        return A.left <= B.left && Aright <= Bright && B.left - Aright <= xMargin;
      };

      const inside = (A: LocalRect, B: LocalRect) =>
        A.top > B.top &&
        A.left > B.left &&
        A.top + A.height < B.top + B.height &&
        A.left + A.width < B.left + B.width;

      const extendWidth = (A: LocalRect, B: LocalRect) => {
        A.width = Math.max(B.width - A.left + B.left, A.width);
      };

      // Remove rects that are inside other rects
      const toRemove = new Set<LocalRect>();
      const firstPass = rects.filter(rect => {
        return rects.every(otherRect => !inside(rect, otherRect));
      });

      // Merge overlapping and adjacent rects (max 3 passes)
      let passCount = 0;
      while (passCount <= 2) {
        for (const A of firstPass) {
          for (const B of firstPass) {
            if (A === B || toRemove.has(A) || toRemove.has(B)) {
              continue;
            }

            if (!sameLine(A, B)) {
              continue;
            }

            if (overlaps(A, B)) {
              extendWidth(A, B);
              A.height = Math.max(A.height, B.height);
              toRemove.add(B);
            }

            if (nextTo(A, B)) {
              extendWidth(A, B);
              toRemove.add(B);
            }
          }
        }
        passCount += 1;
      }

      return firstPass.filter(rect => !toRemove.has(rect));
    };

    /**
     * Process selection and create strikeouts
     * Main entry point for strikeout creation
     */
    const processStrikeoutSelection = (range: Range, selection: Selection) => {
      try {
        const selectedText = selection.toString().trim();
        if (!selectedText) return;

        // Find the textLayer containing the selection
        let textLayer: HTMLElement | null = null;
        if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
          const element = range.startContainer as HTMLElement;
          if (element.classList.contains('textLayer')) {
            textLayer = element;
          } else {
            textLayer = element.querySelector('.textLayer') as HTMLElement;
          }
        } else {
          // Text node - find parent textLayer
          const parentElement = range.startContainer.parentElement;
          textLayer = parentElement?.closest('.textLayer') as HTMLElement | null;
        }
        if (!textLayer) return;

        const canvasContainer = textLayer.parentElement as HTMLElement | null;
        if (!canvasContainer) return;

        const pageWrapper = canvasContainer.parentElement as HTMLElement | null;
        if (!pageWrapper) return;

        const pageNum = parseInt(pageWrapper.id.replace('page-wrapper-', ''), 10);
        if (isNaN(pageNum)) return;

        // React-pdf-highlighter approach: Direct rect extraction from range
        const rawRects = getClientRectsForRange(range, canvasContainer);
        const rects = optimizeClientRects(rawRects);

        if (rects.length === 0) return;

        // Clear selection
        selection.removeAllRanges();

        // Generate a single groupId for all lines in this selection
        const groupId = `strikeout-group-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        // Create strikeouts from rectangles
        rects.forEach((rect) => {
          // Convert to PDF coordinates (accounting for zoom)
          const x = rect.left / zoom;
          const y = rect.top / zoom;
          const width = rect.width / zoom;
          const height = rect.height / zoom;

          // Position strikeout at 50% of rect height (middle of text)
          const strikeoutY = y + (height * 0.5);

          // Check for overlap with existing strikeouts to avoid duplicates
          const hasOverlap = strikeouts.some(existingStrikeout => {
            if (existingStrikeout.pageNum !== pageNum) return false;

            // Calculate horizontal overlap
            const xOverlap = !(x + width < existingStrikeout.x || x > existingStrikeout.x + existingStrikeout.width);

            // Calculate vertical overlap (strikeouts on same line should be within ~5px)
            const yOverlap = Math.abs(strikeoutY - existingStrikeout.y) < 5;

            return xOverlap && yOverlap;
          });

          if (!hasOverlap) {
            handleAddStrikeout({
              pageNum,
              x,
              y: strikeoutY,
              width,
              height,
              text: selectedText.substring(0, 100),
              groupId,
            });
          }
        });
      } catch (error) {
        console.error('Error creating strikeout:', error);
      }
    };

    // Listen to mouseup events on the container - only process when user finishes selecting
    if (container) {
      container.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (selectionTimeout) clearTimeout(selectionTimeout);
      if (container) {
        container.removeEventListener('mouseup', handleMouseUp);
      }
      observer.disconnect();
      // Clean up - remove strikeout-mode class from all text layers
      updateTextLayers();
    };
  }, [activeAnnotationTool, zoom, handleAddStrikeout, strikeouts, containerRef]);

  // Ctrl+Z undo handler for strikeouts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Ctrl+Z when strikeout mode is active
      if (activeAnnotationTool !== 'strikeout') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();

        if (strikeoutHistory.length > 0) {
          // Get last strikeout ID from history
          const lastId = strikeoutHistory[strikeoutHistory.length - 1];

          // Remove from strikeouts
          handleDeleteStrikeout(lastId);

          // Remove from history
          setStrikeoutHistory(prev => prev.slice(0, -1));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeAnnotationTool, strikeoutHistory, handleDeleteStrikeout]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`${styles.viewer} ${className || ''}`}>
        <div className={styles.loadingContainer}>
          <p>Loading PDF...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${styles.viewer} ${className || ''}`}>
        <div className={styles.errorContainer}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.viewer} ${className || ''}`} ref={containerRef}>
      {/* Toolbar - Conditional based on mode */}
      {showControls && mode === 'edit' && (
        <DocumentEditorToolbar
          documentId={documentId}
          documentTitle={documentTitle}
          lastSaved={lastSaved}
          thumbnailsVisible={thumbnailsVisible}
          onToggleThumbnails={() => setThumbnailsVisible(!thumbnailsVisible)}
          showThumbnailToggle={showThumbnails}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          zoom={zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          onSearch={handleSearch}
          searchResultsCount={searchMatches.length}
          currentSearchResult={currentSearchResultIndex + 1}
          onNextSearchResult={nextSearchResult}
          onPreviousSearchResult={previousSearchResult}
          onDownloadPdf={handleDownloadPdfWithFields}
          activeAnnotationTool={activeAnnotationTool}
          onSelectAnnotationTool={handleSelectAnnotationTool}
          formBuilderActive={formBuilderActive}
          onToggleFormBuilder={handleToggleFormBuilder}
          activeFormFieldTool={activeFormFieldTool}
          onSelectFormFieldTool={handleSelectFormFieldTool}
          counterpartyPermissionLevel={counterpartyPermissionLevel}
          activePermissionLevel={activePermissionLevel}
          canAccessFormsToggle={canAccessFormsToggle}
          onChangePermissions={handleChangePermissions}
          canEditPermissions={canEditPermissions}
          commentsVisible={commentsVisible}
          onToggleComments={handleToggleComments}
        />
      )}

      {/* Viewer Controls - For read-only and other modes */}
      {showControls && mode !== 'edit' && (
        <ViewerControls
          currentPage={currentPage}
          totalPages={totalPages}
          zoom={zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          onPageChange={goToPage}
          onSearch={handleSearch}
          searchResultsCount={searchMatches.length}
          currentSearchResult={currentSearchResultIndex + 1}
          onNextSearchResult={nextSearchResult}
          onPreviousSearchResult={previousSearchResult}
          thumbnailsVisible={thumbnailsVisible}
          onToggleThumbnails={() => setThumbnailsVisible(!thumbnailsVisible)}
          showThumbnailToggle={showThumbnails}
        />
      )}

      {/* Main content with optional sidebar */}
      <div className={styles.contentWrapper}>
        {/* Thumbnail Sidebar */}
        {showThumbnails && thumbnailsVisible && pagesInfo.length > 0 && (
          <ThumbnailSidebar
            pagesInfo={pagesInfo}
            currentPage={currentPage}
            onPageClick={goToPage}
            pdfDocument={pdfDocument}
            canEditPages={canEditPages(activePermissionLevel)}
            onInsertPage={handleInsertPage}
            onDeletePage={handleDeletePage}
            onReorderPages={handleReorderPages}
          />
        )}

        {/* Pages container - zoom handled by re-rendering pages at different scales */}
        <div
          className={`${styles.pagesContainer} ${commentsVisible ? styles.pagesContainerWithSidebar : ''}`}
          ref={pagesContainerRef}
        >
          {pagesInfo.map((pageInfo) => (
            <div
              key={pageInfo.pageNumber}
              id={`page-wrapper-${pageInfo.pageNumber}`}
              className={styles.pageWrapper}
            >
              {/* Canvas for PDF rendering */}
              <div
                className={`${styles.canvasContainer} ${
                  activeFormFieldTool === 'textfield' || activeFormFieldTool === 'checkbox'
                    ? styles.cursorCrosshair
                    : activeAnnotationTool === 'freetext'
                    ? styles.cursorText
                    : activeAnnotationTool === 'strikeout'
                    ? styles.cursorText
                    : ''
                }`}
                onClick={(e) => handlePageClick(e, pageInfo.pageNumber)}
              >
                <canvas
                  id={`pdf-page-${pageInfo.pageNumber}`}
                  className={styles.pdfCanvas}
                />

                {/* Search highlights */}
                {searchMatches.length > 0 && (
                  <SearchHighlight
                    pageNumber={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    matches={searchMatches}
                    currentMatchIndex={currentSearchResultIndex}
                    scale={pageInfo.scale}
                  />
                )}

                {/* Annotation overlays */}
                <AnnotationOverlay
                  pageNumber={pageInfo.pageNumber}
                  pageWidth={pageInfo.width}
                  pageHeight={pageInfo.height}
                  annotations={initialAnnotations.filter(a => a.page === pageInfo.pageNumber)}
                  mode={mode}
                  onAnnotationsChange={onAnnotationsChange}
                  onFieldClick={onFieldClick}
                  zoom={zoom}
                />

                {/* Field overlay for signing-prep mode */}
                {fieldOverlayProps && (
                  <FieldOverlay
                    pageNumber={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    fields={fieldOverlayProps.fields}
                    selectedFieldId={fieldOverlayProps.selectedFieldId}
                    onSelectField={fieldOverlayProps.onSelectField}
                    onUpdateField={fieldOverlayProps.onUpdateField}
                    onDeleteField={fieldOverlayProps.onDeleteField}
                    zoom={zoom}
                    readOnlyMode={fieldOverlayProps.readOnlyMode}
                  />
                )}

                {/* Form field overlay for filling mode (NOT builder mode)
                    UNIFIED ARCHITECTURE: Uses allFields directly (single source of truth)
                    Renders all fields with interactive filling capability
                    HIDDEN for flattened PDFs (PREPARING_TO_SIGN, SIGNING, EXECUTED) - fields are baked into PDF
                */}
                {!hideFormFieldsAndAnnotations && allFields.length > 0 && !formBuilderActive && (
                  <FormFieldOverlay
                    key={`form-overlay-${pageInfo.pageNumber}-${pageRevision}`}
                    pageNumber={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    formFields={allFields}
                    onUpdateValue={handleUpdateFieldValue}
                    zoom={zoom}
                    readOnly={formFieldsReadOnly}
                  />
                )}

                {/* Builder form fields overlay - user-created fields (ONLY in Forms mode)
                    Forms mode: Show editable builder overlay for adding/editing field positions
                    Annotate mode: Hide builder overlay (FormFieldOverlay shows interactive fillable fields)
                */}
                {allFields.length > 0 && formBuilderActive && (
                  <BuilderFieldsOverlay
                    key={`builder-overlay-${pageInfo.pageNumber}-${pageRevision}`}
                    pageNum={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    fields={allFields}
                    selectedFieldId={selectedFieldId}
                    zoom={zoom}
                    readOnly={false}
                    onFieldClick={handleFieldClick}
                    onFieldSelect={handleFieldSelect}
                    onFieldPositionChange={handleFieldPositionChange}
                    onFieldSizeChange={handleFieldSizeChange}
                  />
                )}

                {/* Comments overlay - show comment markers (hidden during signing & zooming) */}
                {!hideComments && !isZooming && (
                  <CommentsOverlay
                    comments={comments}
                    pageNum={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    zoom={zoom}
                    onCommentClick={handleCommentClick}
                    onUpdateComment={handleUpdateComment}
                    onAddCommentAt={handleAddCommentAt}
                    addCommentMode={activeAnnotationTool === 'comment'}
                  />
                )}

                {/* Strikeout overlay - text selection for strikeouts
                    HIDDEN for flattened PDFs (PREPARING_TO_SIGN, SIGNING, EXECUTED) - strikeouts are baked into PDF
                */}
                {!hideFormFieldsAndAnnotations && !isZooming && (
                  <StrikeoutOverlay
                    strikeouts={strikeouts}
                    pageNum={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    zoom={zoom}
                    strikeoutMode={activeAnnotationTool === 'strikeout'}
                    onAddStrikeout={handleAddStrikeout}
                    onDeleteStrikeout={handleDeleteStrikeout}
                    canDeleteStrikeouts={canEditPages(activePermissionLevel)}
                  />
                )}

                {/* Free text overlay - editable text annotations
                    HIDDEN for flattened PDFs (PREPARING_TO_SIGN, SIGNING, EXECUTED) - free text is baked into PDF
                */}
                {!hideFormFieldsAndAnnotations && !isZooming && (
                  <FreeTextOverlay
                    freeTexts={freeTexts}
                    pageNum={pageInfo.pageNumber}
                    pageWidth={pageInfo.width}
                    pageHeight={pageInfo.height}
                    zoom={zoom}
                    addFreeTextMode={activeAnnotationTool === 'freetext'}
                    formBuilderActive={formBuilderActive}
                    onAddFreeTextAt={handleAddFreeTextAt}
                    onUpdateFreeText={handleUpdateFreeText}
                    onUpdateFreeTextProperties={handleUpdateFreeTextProperties}
                    onMoveFreeText={handleMoveFreeText}
                    onResizeFreeText={handleResizeFreeText}
                    onDeleteFreeText={handleDeleteFreeText}
                    onDeactivate={() => setActiveAnnotationTool('none')}
                    canDeleteFreeText={canEditPages(activePermissionLevel)}
                    canEditFreeText={canEditPages(activePermissionLevel)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comments Sidebar (hidden during signing) */}
      {!hideComments && (
        <CommentsSidebar
          visible={commentsVisible}
          onClose={handleToggleComments}
          comments={comments}
          currentUserId={userId}
          currentUserName={userName}
          onAddComment={handleAddComment}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          onCommentClick={handleCommentClick}
          totalPages={totalPages}
          selectedCommentId={selectedCommentId}
        />
      )}

      {/* Field Context Menu - edit/delete options for builder fields */}
      {contextMenuState && (
        <FieldContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          fieldType={allFields.find(f => f.id === contextMenuState.fieldId)?.type || 'textfield'}
          onEdit={handleEditField}
          onDelete={handleDeleteField}
          onClose={() => setContextMenuState(null)}
        />
      )}

      {/* Edit Field Modal - edit field properties */}
      {editingField && (
        <EditFieldModal
          field={editingField}
          onSave={handleSaveFieldEdits}
          onClose={() => setEditingField(null)}
        />
      )}

      {/* Loading overlay for page operations (insert/delete/reorder) */}
      {isPageOperationInProgress && <LoadingSpinner overlay />}
    </div>
  );
};

// Export utility functions for external use
export { exportAnnotations, importAnnotations } from './utils';
